import { useApolloClient, useFragment } from "@apollo/client";
import { useSettings } from "@sinch/core/providers/AppSettings";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import type { ConditionCategory } from "@sinch/types/sinch.types";
import { isNil } from "ramda";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ConditionSubjectValue } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionSubjectSelect/ConditionSubjectSelect";
import {
  getSubjectLabels,
  getValLabel,
  sortSubject,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionSubjectSelect/tools";
import {
  ConditionCategories,
  RateConditions,
  RateConditionSubject,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/queries.graphql";
import {
  ConditionCategoriesQuery,
  ConditionCategoriesQueryVariables,
  RateConditionsQuery,
  RateConditionsQueryVariables,
  RateConditionSubjectFragment,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/queries.types";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";

/**
 * A custom hook to manage and load subject selection options for conditional rate configurations.
 * - `options`: The nested list of selectable options for condition subjects, dynamically populated based on data.
 */
export const useSubjectSelect = (value: ConditionSubjectValue | null = null) => {
  const { locale } = useSettings();
  const { t } = useTranslation();
  const client = useApolloClient();
  const { rateType } = useRateBuilderSettings();
  const { data } = useSuspenseQueryHandler<ConditionCategoriesQuery, ConditionCategoriesQueryVariables>(
    ConditionCategories,
    {
      ratelistType: rateType,
    }
  );
  const { data: subjectData, dispatch } = useLazyQueryHandler<RateConditionsQuery, RateConditionsQueryVariables>(
    RateConditions
  );
  // The `RateConditionSubject:...` cache entries that `useSelectedRateConditionSubject` reads via
  // useFragment are written manually here - they are not produced by normalizing the RateConditions
  // query (that uses a different cache id). This has to run on BOTH the freshly-loaded and the
  // already-cached path: otherwise a condition whose RateConditions were cached before the subject
  // select mounted (e.g. by the view-mode overview via `useRateConditions`) would never resolve its
  // valueType, leaving the condition value input stuck disabled after switching from view to edit.
  const writeSubjectFragments = useCallback(
    (category: ConditionCategory, rateConditions: RateConditionsQuery["rateConditions"] | null | undefined) => {
      for (const rateConditionData of rateConditions ?? []) {
        client.writeFragment<RateConditionSubjectFragment>({
          id: `RateConditionSubject:${category}:${rateConditionData.type}:${rateConditionData.subjectId}`,
          fragment: RateConditionSubject,
          data: rateConditionData,
        });
      }
    },
    [client]
  );

  const loadSubjects = (category: ConditionCategory) =>
    dispatch({ category: category }).then((subjects) => writeSubjectFragments(category, subjects?.data?.rateConditions));

  const loadFromCacheOrPostpone = useCallback(
    (category: ConditionCategory) => {
      const cachedSubjects = client.readQuery<RateConditionsQuery, RateConditionsQueryVariables>({
        query: RateConditions,
        variables: {
          category: category,
        },
      });

      if (isNil(cachedSubjects)) {
        const promiseReturn = () => loadSubjects(category);
        if (value?.category === category) {
          promiseReturn();
        }
        return promiseReturn;
      }
      writeSubjectFragments(category, cachedSubjects.rateConditions);
      const rateConditionOptions = getSubjectLabels(category, cachedSubjects?.rateConditions);
      return sortSubject(rateConditionOptions, locale);
    },
    [client, value?.category, dispatch, locale, subjectData, writeSubjectFragments]
  );

  const syncOptions = useMemo(
    () =>
      data?.rateConditionCategories.map(({ category, label, rateConditionSubject }) => {
        if (rateConditionSubject !== null) {
          client.writeFragment<RateConditionSubjectFragment>({
            id: `RateConditionSubject:${category}:${rateConditionSubject.type}:${rateConditionSubject.subjectId}`,
            fragment: RateConditionSubject,
            data: rateConditionSubject,
          });
          return getValLabel(
            category,
            rateConditionSubject.type,
            rateConditionSubject.subjectId,
            rateConditionSubject.label
          );
        }
        return {
          ...getValLabel(category, null, null, label),
          children: loadFromCacheOrPostpone(category),
        };
      }) ?? [],
    [data?.rateConditionCategories, loadFromCacheOrPostpone, client]
  );

  const fullOptions = useMemo(
    () => [{ label: t("RateBuilder.noConditionOption"), value: null }, ...syncOptions],
    [syncOptions, t]
  );

  return { options: fullOptions as typeof syncOptions };
};
/**
 * Generates and returns the selected rate condition subject data based on the provided ConditionSubjectValue.
 */
export const useSelectedRateConditionSubject = (value: ConditionSubjectValue | null = null) => {
  const selectedRateConditionSubject = useFragment<RateConditionSubjectFragment>({
    fragment: RateConditionSubject,
    fragmentName: "RateConditionSubject",
    from: `RateConditionSubject:${value?.category}:${value?.type}:${value?.subjectId}`,
  });

  return selectedRateConditionSubject?.data ?? null;
};
